import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Megaphone } from 'lucide-react';
import { Button } from '../atoms/Button/Button';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../i18n/useTranslation';
import { fetchInAppNews, markInAppNewsSeen, NEWS_QUERY_KEY, type InAppNews } from '../../services/newsService';
import { NewsDialog } from './NewsDialog';
import './NewsButton.css';

/**
 * Header icon next to the language switcher. A dot marks news the user has not
 * seen yet — never colour-only: the button's name then says "Neuigkeiten, neu".
 * Opening the dialog marks everything as seen (per user, on the server); the
 * "Neu" badges stay visible until the dialog is closed.
 */
export function NewsButton() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const news = useQuery({
    queryKey: NEWS_QUERY_KEY,
    queryFn: fetchInAppNews,
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });
  const markSeen = useMutation({
    mutationFn: markInAppNewsSeen,
    onSuccess: () => {
      queryClient.setQueryData<InAppNews>(NEWS_QUERY_KEY, (old) => (old ? { ...old, unread: false } : old));
    },
  });

  if (!isAuthenticated) return null;

  const unread = news.data?.unread === true;

  function openDialog(): void {
    setOpen(true);
    if (unread) markSeen.mutate();
  }

  function closeDialog(): void {
    setOpen(false);
    // The next open shows the notes without "Neu" badges.
    void queryClient.invalidateQueries({ queryKey: NEWS_QUERY_KEY });
  }

  return (
    <>
      <Button
        variant="ghost"
        className="news-button"
        aria-label={unread ? t('news.buttonLabelUnread') : t('news.buttonLabel')}
        aria-haspopup="dialog"
        onClick={openDialog}
        data-testid="news-button"
      >
        <Megaphone size={20} aria-hidden="true" />
        {unread && <span className="news-button__dot" aria-hidden="true" data-testid="news-button-dot" />}
      </Button>
      <NewsDialog
        isOpen={open}
        onClose={closeDialog}
        isLoading={news.isPending}
        isError={news.isError}
        onRetry={() => void news.refetch()}
        notes={news.data?.notes ?? []}
      />
    </>
  );
}
